import React from 'react';
import { connect } from 'react-redux';
import Head from 'next/head';

import classNames from 'classnames';
import { loadVideo } from 'redux/modules/video';
import {
  setPageView,
  setVertical,
} from 'redux/modules/shared';

import { setLinkHeaders } from 'lib/setLinkHeaders';

import AdsBundle from 'components/AdsBundle';
import { ThemesCssBundle } from 'components/ThemesCssBundle';
import { AnalyticsLaunchScripts } from 'components/AnalyticsLaunchScripts';
import InlineVideo from 'components/InlineVideo';
import IconfontStyleTag from 'components/IconfontStyleTag';
import { VideoObjectLDScripts } from 'components/VideoObjectLDScripts';

import { logError } from 'lib/datadog';
import ErrorPage from '../_error';
import 'assets/styles/main.scss';
import 'assets/styles/toolkit.scss';
import styles from './videoEmbed.module.scss';
import globalContainerStyles from '../globalContainerStyles.module.scss';


const mapStateToProps = ({
  video,
}) => ({
  nowPlaying: video.current,
});

/**
 * @param {object} props
 * @param {object} props.layout
 * @param {boolean} props.autoPlay
 * @param {object} props.nowPlaying
 * @param {string} vertical
 * @param {number} statusCode
 */
const VideoEmbedPage = ({
  layout,
  autoPlay = false,
  nowPlaying,
  vertical = '',
  statusCode = 200,
}) => {
    if (nowPlaying.error) {
      logError('videoNowPlayingError', nowPlaying.error);
      return (
        <ErrorPage
          statusCode={statusCode === 404 ? 404 : 500}
          vertical={vertical}
          layout={layout}
        />
      );
    }

    return (
      <>
        <Head>
          <meta name="robots" content="noindex" key="robots" />
          <meta name="googlebot" content="indexifembedded" />
          <IconfontStyleTag />
        </Head>
        <VideoObjectLDScripts videos={[nowPlaying]} vertical={vertical} />
        <ThemesCssBundle vertical={vertical} />
        <div className={classNames(globalContainerStyles.container)}>

          <div className={`df items-center justify-center ${styles.embeddedWrapper}`}>
            <div className={styles.playerWrapper}>
              <InlineVideo
                autoPlay={autoPlay}
                isEmbedded
                mute={autoPlay}
                video={nowPlaying}
                loadingLazy={false}
              />
            </div>
          </div>
        </div>

        <AnalyticsLaunchScripts />
        <AdsBundle />
      </>
    );
};

VideoEmbedPage.getInitialProps = async (ctx) => {
  const {
    req: {
      params: {
        id,
        vertical: verticalFromRoute,
      },
      query: {
        autoplay: autoPlay,
      },
    },
    store,
    res: {
      locals: {
        vertical: parsedVertical,
      },
    },
  } = ctx;

  const promises = [
    store.dispatch(loadVideo(id, verticalFromRoute)),
    store.dispatch(setPageView('embedded-video')),
  ];

  if (verticalFromRoute) {
    promises.push(store.dispatch(setVertical(verticalFromRoute)));
  }

  await Promise.all(promises);

  const storeState = store.getState();
  const { video } = storeState;
  const { current } = video;

  const { status: statusCode } = current.error ? current.error : { statusCode: 200 };

  // verify the vertical for the embedded video if the vertical is not defined
  // by url: www.today.com/embedded-video/mmvo62780997696
  const slug = storeState?.video?.current?.taxonomy?.primaryVertical?.slug // if no vertical found, use vertical parsed from url as default
  // vertical parsed from url will be the vertical for the domain as a fallback
  ?? parsedVertical;

  if (!verticalFromRoute) {
    await store.dispatch(setVertical(slug));
  }

  setLinkHeaders(ctx.res, slug);

  return {
    autoPlay,
    statusCode,
    vertical: parsedVertical,
  };
};

export default connect(mapStateToProps)(VideoEmbedPage);

// exported for testing
export { VideoEmbedPage as UnwrappedVideoEmbedPage };
